import {
  mdiPlus,
  mdiArrowBottomLeft,
  mdiClose,
  mdiHistory,
  mdiTrashCan,
  mdiPlusBox,
  mdiArrowTopRight,
  mdiFilterVariant,
} from "@mdi/js";
import Icon from "@mdi/react";
import axios, { CancelTokenSource } from "axios";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import LoadingWidget from "../../../context_providers/modal/loader";
import { ModalType } from "../../../context_providers/modal/modal_context";
import ModalWidget from "../../../context_providers/modal/modal_widget";
import {
  useToast,
  ToastType,
} from "../../../context_providers/toast/toast_ctx";
import { fetchPost } from "../../../service/api_client";
import { urls } from "../../../utils/urls";
import { ItemFilterData, ItemModelV2 } from "../models/item_model";
import ImportWizard from "./components/import_comp";
import {
  bulkApproveItemList,
  getItemsRepo,
} from "./repository/items_repository";
import { useAuth } from "../../../context_providers/auth/authProvider";
import {
  ACTION,
  MasterItemStatus,
  MasterItemStatusDict,
  MODULE,
} from "../../../utils/enums";
import Pagination from "../../../ui/new_components/common/pagination";
import { debounce } from "lodash";
import {
  ItemTypeList,
  ProductItemCreationSubCategoryElectricRawMaterials,
  ServiceItemCreationSubCategory,
} from "./enums/enums";
import ItemSearchField from "./components/ItemSearchField";
import DrawerModal, {
  DrawerModalLoaction,
} from "../../../ui/new_components/common/drawer_modal";
import ItemActionHistory from "./components/item_action_history";
import EmployeeSearchWidget from "../../../ui/new_components/emp_search_widget";
import { UserModel } from "../../../context_providers/auth/user_model";
import { ExportToCsv } from "export-to-csv";
import {
  ResponsiveListShell,
  FilterSheet,
  FilterClearButton,
  MobileRecordCard,
  MobileRecordCardList,
  StatusBadge,
  EmptyState,
} from "../../../ui/new_components/common/responsive";

interface ItemListScreenProps {}

const defaultFilter: ItemFilterData = {
  page: 1,
  count: 100,
};

const ItemListScreen: FunctionComponent<ItemListScreenProps> = () => {
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/items`;

  const [filter, setFilter] = useState<ItemFilterData>(defaultFilter);
  const { showToast } = useToast();
  const [checker, setChecker] = useState(false);
  const [maker, setMaker] = useState(false);
  const [selectedType, setSelectedType] = useState<string>("");
  const [errorObjectList, setErrorObjectList] = useState<string[]>([]);
  const [existingItemList, setExistingItemList] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(50);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState<ItemModelV2[]>([]);
  const [exportData, setExportData] = useState<ItemModelV2[]>([]);
  const [selectedEmp, setSelectedEmp] = useState<UserModel | null>();
  const { isAuthorised } = useAuth();
  const navaigation = useHistory();
  const [showHistory, setShowHistory] = useState<any>();
  const [showImport, setShowImport] = useState(false);
  const [selectedItems, setSelectedItems] = useState<ItemModelV2[]>([]);
  const [showImportErrors, setShowImportErrors] = useState<boolean>(false);
  const [showFilter, setShowFilter] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 768,
  );
  let cancelTokenSource: CancelTokenSource;

  const fetchData = async (prop: ItemFilterData) => {
    setLoading(true);
    try {
      const res = await getItemsRepo(prop);
      if (res.success) {
        setData(res.data.data);

        if (res.data.metadata) {
          setCount(res.data.metadata.count);
          setTotal(res.data.metadata.total);
        }
        setLoading(false);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
    }
  };

  useEffect(() => {
    const checker_permission_result = isAuthorised({
      module: MODULE.master_item_checker,
      action: ACTION.UPDATE,
    });
    const maker_permission_result = isAuthorised({
      module: MODULE.master_item_maker,
      action: ACTION.UPDATE,
    });
    setChecker(checker_permission_result);
    setMaker(maker_permission_result);
  }, [isAuthorised]); // Separate call for permission check

  const debouncedHandler = useCallback(
    debounce((prop: ItemFilterData) => {
      if (prop) fetchData(prop);
    }, 500),
    [],
  );

  useEffect(() => {
    filter.type = selectedType;
    debouncedHandler(filter);
  }, [page, filter, selectedType, debouncedHandler]);

  const onSelect = (d: ItemModelV2) => {
    if (
      checker &&
      (d.status === MasterItemStatus.Approved ||
        d.status === MasterItemStatus.Rejected ||
        d.status === MasterItemStatus.SavedDraft)
    ) {
      navaigation.push(urls.procurement_masters_item + "/view-item/" + d.id);
    } else if ((checker && !maker) || (checker && maker)) {
      navaigation.push(
        urls.procurement_masters_item + "/approve-reject-item/" + d.id,
      );
    } else if (maker && d.status === MasterItemStatus.SavedDraft) {
      navaigation.push(urls.procurement_masters_item + "/update/" + d!.id);
    } else if (
      maker &&
      (d.status === MasterItemStatus.UpdatePending ||
        d.status === MasterItemStatus.PendingForApproval)
    ) {
      navaigation.push(urls.procurement_masters_item + "/view-item/" + d!.id);
    } else {
      navaigation.push(urls.procurement_masters_item + "/view-item/" + d.id);
    }
  };

  const handleOnEdit = () => {
    if (selectedItems.length > 1) {
      showToast({
        type: ToastType.error,
        text: "Please select only one item to edit",
      });
    } else if (selectedItems.length === 0) {
      showToast({
        type: ToastType.error,
        text: "Please select an item to edit",
      });
    } else {
      navaigation.push(
        urls.procurement_masters_item + "/update/" + selectedItems[0]!.id,
      );
    }
  };

  const handleOnDelete = () => {
    if (selectedItems.length > 1) {
      showToast({
        type: ToastType.error,
        text: "Please select  only one item to delete",
      });
    } else if (selectedItems.length === 0) {
      showToast({
        type: ToastType.error,
        text: "Please select an item to delete",
      });
    } else {
      navaigation.push(
        urls.procurement_masters_item + "/delete/" + selectedItems[0]!.id,
      );
    }
  };

  const handleBulkApproval = async () => {
    try {
      setLoading(true);
      const res = await bulkApproveItemList(selectedItems);

      if (res.success) {
        showToast({ type: ToastType.success, text: "Approved Succesfully" });
        fetchData(filter);
      } else {
        showToast({ type: ToastType.error, text: res.error });
        setLoading(false);
      }
    } catch (error: any) {
      setLoading(false);
      showToast({ type: ToastType.error, text: error.message });
    }
  };

  const downloadExistingDevices = () => {
    const csvHeader = ["Type", "Item Name"];

    const formatCsvValue = (value: string) => `"${value.replace(/"/g, '""')}"`; // Escape quotes if needed

    const existingItems = existingItemList.map((item) => [
      "Existing Item",
      formatCsvValue(item),
    ]);
    const errorItems = errorObjectList.map((item) => [
      "Validation Error",
      formatCsvValue(item),
    ]);

    const csvContent = [
      csvHeader.map(formatCsvValue).join(","),
      ...existingItems.map((row) => row.join(",")),
      ...errorItems.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "item_list_errors.csv";
    link.click();
    URL.revokeObjectURL(url); // Clean up the URL object
  };

  const onImport = async (d: {
    data: ItemModelV2[];
    setCompleted: (loading: boolean) => void;
    setUpLoading: (loading: boolean) => void;
    setProgress: (loading: number) => void;
    // setCancelled: (d: boolean) => void;
  }) => {
    cancelTokenSource = axios.CancelToken.source();
    d.setUpLoading(true);
    const config = {
      cancelToken: cancelTokenSource.token,
      onUploadProgress: (e: any) => {
        const percentCompleted = Math.round((e.loaded * 100) / e.total);
        d.setProgress(percentCompleted);
        console.log("uploaded " + percentCompleted + " %");
      },
    };

    const form = new FormData();
    form.append("data", JSON.stringify(d.data));
    const res = await fetchPost(url + "/import", form, {}, config);
    if (res.success) {
      await fetchData({ page, count });

      showToast({ type: ToastType.success, text: res.message });
    } else {
      setShowImport(false);
      if (res.exisiting_items.length > 0) {
        let existing_items: string[] = res.exisiting_items;
        setExistingItemList(existing_items);
        setShowImportErrors(true);
      }
      if (res.validation_errors.length > 0) {
        let validation_errors: string[] = res.validation_errors;
        setErrorObjectList(validation_errors);
        setShowImportErrors(true);
      }
      showToast({ type: ToastType.error, text: res.error });
    }
    d.setUpLoading(false);
  };

  const ExportItemListData = async () => {
    setLoading(true);

    try {
      const searchProp = { ...filter };
      searchProp.all = true;
      const res = await getItemsRepo(searchProp);

      if (res.success) {
        console.log(res.data.data);
        if (res.data.data.length === 0) {
          throw new Error("No data found");
        }
        setExportData(res.data.data);
        if (res.data) {
          const options = {
            filename: `Item Data `,
            fieldSeparator: ",",
            quoteStrings: '"',
            decimalSeparator: ".",
            showLabels: true,
            showTitle: false,
            title: "Item Data",
            useTextFile: false,
            useBom: true,
            useKeysAsHeaders: true,
          };

          const csvExporter = new ExportToCsv(options);

          csvExporter.generateCsv(
            res.data.data.map((data: ItemModelV2, i: number) => {
              return {
                "Item Code": data.item_code,
                "Item Name": data.name ?? "--",
                Description: data.description ?? "--",
                "Item Type": data.type ?? "--",
                Category: data.category ?? "--",
                "Sub-category": data.subcategory ?? "--",
                manufacturer: data.manufacturer ?? "--",
                MPN: data.mpn ?? "--",
                Components: data.components ?? "--",
                "Components Sub Type": data.component_sub_type ?? "--",
                "Mounting Style": data.mounting_style ?? "--",
                Value: data.value ?? "--",
                Footprint: data.footprint ?? "--",
                "Unit Measure": data.unit?.formal_name ?? "--",
                "Tax Applicable": data.tax_applicable ? "Yes" : "No",
                "Tax Type": data.tax_applicable
                  ? (data.tax_detail?.type ?? "--")
                  : "--",
                "GST Rate (%)": data.tax_applicable
                  ? (data.tax_detail?.gst_rate ?? "--")
                  : "--",
                "HSN/SAC Code": data.tax_applicable
                  ? (data.tax_detail?.hsn_sac ?? "--")
                  : "--",
                "Created At": data.created_at ?? "--",
                "Created By": data.created_by_name ?? "--",
                Status: data.status,
              };
            }),
          );
        }
      } else {
        console.log(res.error);
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
      //   return { success: false, error: error.message };
    }
    setLoading(false);
  };

  return (
    <>
      {showHistory && (
        <DrawerModal
          location={DrawerModalLoaction.right}
          show={showHistory !== undefined}
          onClose={() => setShowHistory(false)}
          title="History"
        >
          <ItemActionHistory item_code={showHistory} />
        </DrawerModal>
      )}
      {showImport && (
        <ModalWidget
          data={{
            id: 1,
            title: "Import",
            type: ModalType.modal,

            onClose: () => {
              setShowImport(false);
            },
          }}
          onClose={function (): void {
            setShowImport(false);
          }}
        >
          <ImportWizard
            onClose={() => {
              setShowImport(false);
            }}
            onCancel={function () {
              if (cancelTokenSource) {
                console.log("Cancelled by user");
                cancelTokenSource.cancel("cancelled by user");
              }
            }}
            onSubmit={onImport}
            onError={function (error: string): void {
              showToast({ type: ToastType.error, text: error });
            }}
          />
        </ModalWidget>
      )}

      <ResponsiveListShell
        title="Items"
        count={total}
        onRefresh={() => fetchData({ page, count })}
        search={
          <div id="item_list_item_code_search">
            <ItemSearchField
              defaulValue={filter.item_code ?? ""}
              onClear={() =>
                setFilter((prevData) => ({ ...prevData, item_code: "" }))
              }
              onSubmit={(value: string) =>
                setFilter((prevData) => ({ ...prevData, item_code: value }))
              }
            />
          </div>
        }
        actions={
          <>
            <button
              id="export_btn"
              onClick={() => {
                ExportItemListData();
              }}
              className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-white transform text-xs"
            >
              <Icon path={mdiArrowTopRight} className="h-4 w-4"></Icon>Export
            </button>
            {maker && !checker && (
              <button
                id="import_btn"
                onClick={() => {
                  setShowImport(true);
                }}
                className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-white transform text-xs"
              >
                <Icon path={mdiArrowBottomLeft} size={0.8} />
                Import
              </button>
            )}
            {checker && (
              <>
                {selectedItems.length > 0 && (
                  <button
                    id="bulk_approve_btn"
                    onClick={handleBulkApproval}
                    className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-white transform text-xs"
                  >
                    <Icon path={mdiPlusBox} className="h-4 w-4"></Icon>
                    Bulk Approve
                  </button>
                )}
                <button
                  id="archive_btn"
                  onClick={handleOnDelete}
                  className="flex gap-1 items-center cursor-pointer rounded p-1 bg-red-400 hover:scale-105 duration-200 text-white transform text-xs"
                >
                  <Icon path={mdiTrashCan} className="h-4 w-4"></Icon>Archive
                </button>
              </>
            )}
            {!checker && maker && (
              <>
                <button
                  id="update_btn"
                  onClick={handleOnEdit}
                  className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-white transform text-xs"
                >
                  <Icon path={mdiPlus} className="h-4 w-4"></Icon>Update
                </button>
                <button
                  id="create_btn"
                  onClick={() => {
                    navaigation.push(urls.procurement_masters_item + "/create");
                  }}
                  className="flex gap-1 items-center cursor-pointer rounded p-1 bg-myPrimaryColor hover:scale-105 duration-200 text-white transform text-xs"
                >
                  <Icon path={mdiPlus} className="h-4 w-4"></Icon>Create
                </button>
              </>
            )}
          </>
        }
        filterToggle={
          <div className="flex gap-2 items-center">
            <h6 className="font-normal text-xs">Filters</h6>
            <div
              id="item_list_filter_toggle_btn"
              onClick={() => setShowFilter((v) => !v)}
              className="flex items-center justify-center bg-gray-200 p-1 rounded-full hover:bg-gray-800 hover:text-white cursor-pointer"
            >
              <Icon path={mdiFilterVariant} className="w-4 h-4" />
            </div>
          </div>
        }
      >
        {showImportErrors && errorObjectList.length > 0 && (
          <div className="rounded-lg border border-red-800 m-2 px-4 py-3">
            <div className="flex justify-between">
              <h2 className="text-lg font-bold">
                Some Items Already Exist or validation errors are there
              </h2>
              <div
                id="close_import_errors_btn"
                onClick={() => {
                  setShowImportErrors(false);
                }}
                className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded"
              >
                <Icon path={mdiClose} className="h-4 w-4"></Icon>
              </div>
            </div>
            <p className="mt-2">
              The following Items already exist/validation errors are there in
              the inventory. Please review the list.
            </p>

            <button
              id="download_item_ids_btn"
              onClick={downloadExistingDevices}
              className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded"
            >
              Download Item IDs
            </button>
          </div>
        )}

        <FilterSheet
          show={showFilter}
          onClose={() => setShowFilter(false)}
          onApply={() => setShowFilter(false)}
          onReset={() => {
            setFilter(defaultFilter);
            setSelectedType("");
            setSelectedEmp(null);
          }}
          gridClassName="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-2 items-start"
        >
          <div id="item_list_item_name_filter">
            <label htmlFor="item_name">Item Name</label>
            <ItemSearchField
              defaulValue={filter.item_name ?? ""}
              onClear={() => {
                setFilter((prevData) => ({
                  ...prevData,
                  item_name: "",
                }));
              }}
              onSubmit={(val) => {
                setFilter((prevData) => ({
                  ...prevData,
                  item_name: val,
                }));
              }}
            />
          </div>
          <div className="">
            <label htmlFor="item_type" className="">
              Item Type
            </label>
            <select
              className="w-full bg-white rounded focus:outline-none"
              name="item_type"
              id="item_type"
              value={selectedType ?? ""}
              onChange={(e) => {
                setFilter((prevData) => ({
                  ...prevData,
                  sub_category: "",
                  category: "",
                  type: e.target.value,
                }));
                setSelectedType(e.target.value);
                setSelectedItems([]);
              }}
            >
              <option value="">select</option>
              {ItemTypeList.map((f: string) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
          {selectedType === "Product" && (
            <div>
              <label htmlFor="category" className="">
                Category
              </label>
              <select
                className="w-full rounded bg-white focus:outline-none"
                name="category"
                id="category"
                value={filter.category ?? ""}
                onChange={(e) => {
                  setFilter((prevData) => ({
                    ...prevData,
                    category: e.target.value,
                  }));
                }}
              >
                <option value="">select</option>
                {["Electrical", "Electronics", "Others"].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label htmlFor="app_status" className="">
              Approval Status
            </label>
            <select
              className="w-full rounded focus:outline-none bg-white"
              name="app_status"
              id="app_status"
              value={filter.status || ""}
              onChange={(e) => {
                setFilter((prevData) => ({
                  ...prevData,
                  status: e.target.value,
                }));
              }}
            >
              <option value="">select</option>
              {Object.entries(MasterItemStatusDict).map(([key, value]) => (
                <option key={key} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="sub_category" className="">
              Sub-Category
            </label>
            <select
              className="w-full bg-white rounded focus:outline-none"
              name="sub_category"
              id="sub_category"
              value={filter.sub_category ?? ""}
              onChange={(e) => {
                setFilter((prevData) => ({
                  ...prevData,
                  sub_category: e.target.value,
                }));
              }}
            >
              <option value="">select</option>
              {filter.type === "Product" ? (
                ProductItemCreationSubCategoryElectricRawMaterials.map(
                  (subCategory, index) => (
                    <option key={index} value={subCategory}>
                      {subCategory}
                    </option>
                  ),
                )
              ) : filter.type == "" ? (
                <></>
              ) : (
                ServiceItemCreationSubCategory.map((subCategory, index) => (
                  <option key={index} value={subCategory}>
                    {subCategory}
                  </option>
                ))
              )}
            </select>
          </div>
          <div id="item_list_created_by_filter">
            <label htmlFor="creator_search">Created By</label>
            <div className="flex gap-1 items-center">
              <div className="flex-1">
                <EmployeeSearchWidget
                  withBtn={false}
                  onSelect={(em) => {
                    setFilter((prevData) => ({
                      ...prevData,
                      uid: em.uid,
                    }));
                    setSelectedEmp(em);
                  }}
                  emp={{
                    uid: selectedEmp ? selectedEmp.uid : "",
                    name: selectedEmp
                      ? selectedEmp.first_name + " " + selectedEmp.last_name
                      : "",
                  }}
                  placeholder="Item creator"
                />
              </div>
              {filter.uid && (
                <FilterClearButton
                  title="Clear creator"
                  onClick={() => {
                    setFilter((prevData) => ({
                      ...prevData,
                      uid: "",
                    }));
                    setSelectedEmp(null);
                  }}
                />
              )}
            </div>
          </div>
        </FilterSheet>

        {data.length > 0 ? (
          <>
            <div className="hidden md:block my-2 border">
              <div className="overflow-auto">
                <table
                  id="item_list_table"
                  className="w-full text-xs divide-y divide-gray-200 relative"
                >
                  <thead className="text-xs py-2 bg-white z-10">
                    <tr className="border">
                      <th>
                        <div className="">
                          <input
                            type="checkbox"
                            name=""
                            id=""
                            checked={
                              selectedItems.length === data.length
                                ? true
                                : false
                            }
                            disabled={checker && !maker ? false : true}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedItems([...data]);
                              } else {
                                setSelectedItems([]);
                              }
                            }}
                          />
                        </div>
                      </th>
                      <th className="px-2 py-2">Actions</th>
                      <th className="px-2 py-2 text-left">Item code</th>
                      <th className="px-2 py-2 text-left">Item name</th>
                      <th className="px-2 py-2 text-left">Description</th>
                      <th className="px-2 py-2 text-left">Item type</th>
                      <th className="px-2 py-2 text-left">Manufacturer</th>
                      <th className="px-2 py-2 text-left">MPN</th>
                      <th className="px-2 py-2 text-left">Category</th>
                      <th className="px-2 py-2 text-left">Sub-category</th>
                      <th className="px-2 py-2 text-left">Unit measure</th>
                      <th className="px-2 py-2 text-left">Created By</th>
                      <th className="px-2 py-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody className="">
                    {data.map((item, i) => {
                      const isSelected = selectedItems.includes(item);
                      return (
                        <tr
                          id={`item_row_${item.item_code}`}
                          className="border text-center "
                          key={i}
                        >
                          <td>
                            <div className="p-2">
                              <input
                                type="checkbox"
                                name=""
                                id=""
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedItems((prev) => [...prev, item]);
                                  } else {
                                    setSelectedItems((prev) =>
                                      prev.filter(
                                        (selectedItem) => selectedItem !== item,
                                      ),
                                    );
                                  }
                                }}
                              />
                            </div>
                          </td>
                          <td>
                            <div className="flex gap-2 justify-center">
                              <div
                                id={`item_history_btn_${item.item_code}`}
                                className="cursor-pointer"
                                onClick={() => {
                                  setShowHistory(item.item_code);
                                }}
                              >
                                <Icon path={mdiHistory} size="16"></Icon>
                              </div>
                            </div>
                          </td>
                          <td
                            id={`item_code_link_${item.item_code}`}
                            className="px-3 py-2 text-left cursor-pointer text-blue-600 hover:underline"
                            role="button"
                            onClick={() => onSelect(item)}
                          >
                            {item.item_code}
                          </td>
                          <td className="px-3 py-2 text-left">{item.name}</td>
                          <td className="px-3 py-2 text-left">
                            {item.description ?? "--"}
                          </td>
                          <td className="px-3 py-2 text-left">{item.type}</td>
                          <td className="px-3 py-2 text-left">
                            {item.manufacturer ?? "--"}
                          </td>
                          <td className="px-3 py-2 text-left">
                            {item.mpn ?? "--"}
                          </td>
                          <td className="px-3 py-2 text-left">
                            {item.category ?? "--"}
                          </td>
                          <td className="px-3 py-2 text-left">
                            {item.subcategory ?? "--"}
                          </td>
                          <td className="px-3 py-2 text-left">
                            {item?.unit?.formal_name === null ||
                            item?.unit?.formal_name === undefined
                              ? "--"
                              : item?.unit?.formal_name}
                          </td>
                          <td className="px-3 py-2 text-left">
                            {item.created_by_name ?? "--"}
                          </td>
                          <td className="px-3 py-2 text-left">
                            <StatusBadge status={item.status} kind="generic" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <MobileRecordCardList>
              {data.map((item, i) => (
                <MobileRecordCard
                  id={`item_row_mobile_${item.item_code}`}
                  key={i}
                  onClick={() => onSelect(item)}
                  title={item.item_code}
                  status={<StatusBadge status={item.status} kind="generic" />}
                  fields={[
                    { label: "Name", value: item.name },
                    { label: "Type", value: item.type },
                    { label: "Category", value: item.category ?? "-" },
                    { label: "Sub-category", value: item.subcategory ?? "-" },
                    {
                      label: "Unit",
                      value: item?.unit?.formal_name ?? "-",
                    },
                    {
                      label: "Created By",
                      value: item.created_by_name ?? "-",
                    },
                  ]}
                  actions={[
                    {
                      icon: mdiHistory,
                      title: "History",
                      onClick: () => setShowHistory(item.item_code),
                    },
                  ]}
                />
              ))}
            </MobileRecordCardList>

            <div
              id="item_list_pagination"
              className="mt-3 sticky bottom-0 bg-white z-10 border-t flex justify-between items-center px-4 py-2"
            >
              <Pagination
                itemCount={count}
                page={filter.page}
                total={total}
                count={filter.count}
                onChange={(page) => setFilter((o) => ({ ...o, page }))}
              />
            </div>
          </>
        ) : !loading ? (
          <EmptyState
            title="No Items found!"
            message="Try adjusting your search or filters."
            imageSrc="/images/nodata.png"
          />
        ) : null}
      </ResponsiveListShell>

      {loading && <LoadingWidget></LoadingWidget>}
    </>
  );
};

export default ItemListScreen;
